import { useMemo, useState } from "react";
import { Plus } from "lucide-react";

import {
  type CarrierTransportServiceRow,
  useCarrierCostRates,
  useCarrierDispatchCenters,
  useCarrierServiceAreas,
  useCarrierTransportServices,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EditServiceDialog } from "@/features/edit-service-dialog";
import {
  isServiceOffered,
  ServiceOfferingGuidance,
} from "@/features/service-offering-guidance";
import {
  ShippingServiceCatalogue,
  ShippingServiceDetails,
  ShippingServicesPermissionState,
} from "@/features/shipping-service-catalog";
import { hasHttpStatus } from "@/lib/api-error";

type AvailabilityFilter = "ALL" | "ACTIVE" | "INACTIVE";

/** Carrier-owned label products. Rates for these products live in Pricing. */
export default function ShippingServicesScreen() {
  const services = useCarrierTransportServices();
  const areas = useCarrierServiceAreas({ all: true });
  const rates = useCarrierCostRates();
  const dispatchCenters = useCarrierDispatchCenters();
  const [search, setSearch] = useState("");
  const [availability, setAvailability] = useState<AvailabilityFilter>("ALL");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CarrierTransportServiceRow | null>(
    null
  );
  const [details, setDetails] = useState<CarrierTransportServiceRow | null>(
    null
  );

  const rows = services.data ?? [];
  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return rows.filter((service) => {
      const matchesQuery =
        !query ||
        service.name.toLocaleLowerCase().includes(query) ||
        service.code.toLocaleLowerCase().includes(query);
      const matchesAvailability =
        availability === "ALL" ||
        (availability === "ACTIVE" ? service.isActive : !service.isActive);
      return matchesQuery && matchesAvailability;
    });
  }, [availability, rows, search]);

  const forbidden = hasHttpStatus(services.error, 403);
  const initialLoading = services.isLoading && rows.length === 0;
  const hasFilters = search.trim() !== "" || availability !== "ALL";
  const countLabel = `${rows.length} service${rows.length === 1 ? "" : "s"}`;

  return (
    <div className="carrier-page">
      <PageHeader
        title="Shipping Services"
        actions={
          forbidden ? null : (
            <Button size="touch" onClick={() => setCreating(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add service
            </Button>
          )
        }
      />

      <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Service activation alone does not make a quote available. Offer it in
        Service Areas, price the exact lane or connect a quoting provider, and
        configure delivery runs for carrier-managed services.
      </p>
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
          <div>
            <h2 className="font-semibold">Shipping services</h2>
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {initialLoading ? "Loading…" : countLabel}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              type="search"
              aria-label="Search shipping services"
              placeholder="Search services…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="sm:w-56"
            />
            <Select
              value={availability}
              onValueChange={(value) =>
                setAvailability(value as AvailabilityFilter)
              }
            >
              <SelectTrigger
                aria-label="Filter by availability"
                size="sm"
                className="w-full sm:w-40"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All availability</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {forbidden ? (
          <ShippingServicesPermissionState />
        ) : (
          <>
            {services.error && (
              <div
                role="alert"
                className="flex flex-col gap-3 border-b bg-destructive/5 p-3 text-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <span>Shipping services could not be refreshed.</span>
                <Button
                  variant="outline"
                  size="sm-touch"
                  onClick={() => void services.refetch()}
                >
                  Try again
                </Button>
              </div>
            )}

            {(!services.error || rows.length > 0) && (
              <ShippingServiceCatalogue
                rows={filteredRows}
                initialLoading={initialLoading}
                hasFilters={hasFilters}
                onView={setDetails}
                onEdit={setEditing}
              />
            )}
          </>
        )}
      </Card>

      <DetailDrawer
        open={details !== null}
        onOpenChange={(open) => {
          if (!open) setDetails(null);
        }}
        title={details?.name ?? "Shipping service"}
        description={details?.code}
      >
        {details && (
          <div className="space-y-4">
            <ShippingServiceDetails
              service={details}
              onEdit={
                details.selfRated
                  ? () => {
                      setDetails(null);
                      setEditing(details);
                    }
                  : undefined
              }
            />
            <ServiceOfferingGuidance
              service={details}
              offered={
                areas.data === undefined
                  ? undefined
                  : isServiceOffered(
                      Array.isArray(areas.data) ? areas.data : areas.data.data,
                      details
                    )
              }
              labelBands={rates.data?.labelBands}
              dispatchCenters={dispatchCenters.data}
            />
          </div>
        )}
      </DetailDrawer>

      <EditServiceDialog
        open={creating}
        onOpenChange={setCreating}
        service={null}
      />
      <EditServiceDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        service={editing}
      />
    </div>
  );
}
